import { useEffect, useId, useRef, useState } from "react";
import {
  AspectRatio,
  Box,
  Button,
  Card,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import { Image as ImageIcon } from "@phosphor-icons/react";
import { MediaLibrary } from "./MediaLibrary";
import type { MediaFile } from "./mediaApi";

interface Props {
  value: MediaFile | null;
  onChange: (media: MediaFile | null) => void;
}

// Keep browsing separate from the post draft: Cancel never removes its image.
function ImagePicker({
  value,
  onChange,
  onClose,
}: Props & { onClose: () => void }) {
  const [selection, setSelection] = useState(value);
  const titleId = useId();
  return (
    <Modal open onClose={onClose}>
      <ModalDialog
        aria-labelledby={titleId}
        sx={{
          width: "min(1100px, calc(100vw - 32px))",
          minWidth: 0,
          maxWidth: "none",
          height: "min(860px, calc(100dvh - 32px))",
          maxHeight: "calc(100dvh - 32px)",
          p: 0,
          gap: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 2,
            pr: 6,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography id={titleId} level="h4">
            Choose a post image
          </Typography>
          <Typography level="body-sm">
            Select a photo, then choose Use image to add it to your post.
          </Typography>
          <ModalClose aria-label="Close image picker" />
        </Box>
        <Box
          sx={{
            overflowY: "auto",
            minHeight: 0,
            p: { xs: 1.5, sm: 2.5 },
            flex: 1,
          }}
        >
          <MediaLibrary
            selectionMode
            acceptedTypes={["image"]}
            selectedMedia={selection}
            onSelect={setSelection}
          />
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0 }}
            aria-live="polite"
          >
            {selection && (
              <Box
                component="img"
                src={selection.thumbnail || selection.file}
                alt=""
                sx={{
                  width: 64,
                  height: 48,
                  objectFit: "contain",
                  bgcolor: "background.level1",
                  borderRadius: "sm",
                }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography level="title-sm" noWrap title={selection?.name}>
                {selection?.name || "No image selected"}
              </Typography>
              <Typography level="body-xs">
                {selection
                  ? "Ready to add to your post"
                  : "Choose an image from the library above"}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button type="button" variant="plain" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!selection}
              onClick={() => {
                onChange(selection);
                onClose();
              }}
            >
              Use image
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}

export function GBPPostImageField({ value, onChange }: Props) {
  const [picking, setPicking] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const wasPicking = useRef(false);
  useEffect(() => {
    // Choosing the first photo replaces the opening button. Restore focus to
    // its replacement instead of leaving keyboard users at the top of the form.
    if (wasPicking.current && !picking) trigger.current?.focus();
    wasPicking.current = picking;
  }, [picking]);
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Typography level="title-sm">Post image</Typography>
      {value ? (
        <Card variant="outlined" sx={{ minWidth: 0 }}>
          <AspectRatio ratio="16/9" objectFit="contain">
            <img src={value.file} alt={value.alt_text || value.name} />
          </AspectRatio>
          <Typography level="body-sm" noWrap title={value.name}>
            {value.name}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              type="button"
              size="sm"
              variant="outlined"
              ref={trigger}
              onClick={() => setPicking(true)}
            >
              Change image
            </Button>
            <Button
              type="button"
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => onChange(null)}
            >
              Remove image
            </Button>
          </Stack>
        </Card>
      ) : (
        <Card
          variant="outlined"
          sx={{
            alignItems: "center",
            textAlign: "center",
            py: 3,
            borderStyle: "dashed",
          }}
        >
          <ImageIcon size={32} aria-hidden />
          <Typography level="body-sm">Add a photo to your post</Typography>
          <Button
            type="button"
            variant="outlined"
            ref={trigger}
            onClick={() => setPicking(true)}
          >
            Choose image
          </Button>
        </Card>
      )}
      <Typography level="body-xs">
        Browse marketing uploads and job photos tagged “marketing”, or upload a
        new image.
      </Typography>
      {picking && (
        <ImagePicker
          value={value}
          onChange={onChange}
          onClose={() => setPicking(false)}
        />
      )}
    </Stack>
  );
}
